import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import api from "./api";
import { 
  Pencil, 
  Trash2, 
  Save, 
  X, 
  Mail, 
  Briefcase, 
  Search,
  UserCircle,
  Shield,
  Wrench,
} from "lucide-react";

function TeamList() {
  const [team, setTeam] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({
    full_name: "",
    role: "",
    email: "",
  });

  const fetchTeam = async () => {
    setLoading(true);
    try {
      const res = await api.get("/api/team");
      setTeam(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error("❌ Error fetching team", err);
      alert("❌ Failed to load team list");
      setTeam([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTeam();
  }, []);

  const startEdit = (member) => {
    setEditingId(member.id);
    setEditForm({
      full_name: member.full_name || "",
      role: member.role || "",
      email: member.email || "",
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditForm({ full_name: "", role: "", email: "" });
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditForm((prev) => ({ ...prev, [name]: value }));
  };

  const saveEdit = async (id) => {
    try {
      await api.put(`/api/team/${id}`, editForm);
      cancelEdit();
      fetchTeam();
    } catch (err) {
      console.error("❌ Error updating technician", err);
      alert("❌ Failed to update technician");
    }
  };

  const deleteTech = async (id) => {
    if (!window.confirm("Delete this technician?")) return;
    try {
      await api.delete(`/api/team/${id}`);
      setTeam((prev) => prev.filter((t) => t.id !== id));
    } catch (err) {
      console.error("❌ Error deleting technician", err);
      alert("❌ Failed to delete technician");
    }
  };

  // Filter team based on search
  const filteredTeam = team.filter((member) => {
    const search = searchTerm.toLowerCase();
    return (
      (member.full_name || "").toLowerCase().includes(search) ||
      (member.role || "").toLowerCase().includes(search) ||
      (member.email || "").toLowerCase().includes(search)
    );
  });

  // Get initials for avatar
  const getInitials = (name) => {
    if (!name) return "?";
    const parts = name.trim().split(" ");
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  // Generate avatar color based on name
  const getAvatarColor = (name) => {
    const colors = [
      "#3b82f6", "#8b5cf6", "#ec4899", "#f59e0b", 
      "#10b981", "#06b6d4", "#6366f1", "#ef4444"
    ];
    if (!name) return colors[0];
    const index = name.charCodeAt(0) % colors.length;
    return colors[index];
  };

  // Get role icon
  const getRoleIcon = (role) => {
    const roleLower = (role || "").toLowerCase();
    if (roleLower.includes("manager") || roleLower.includes("supervisor")) {
      return <Shield size={18} />;
    }
    if (roleLower.includes("senior") || roleLower.includes("lead")) {
      return <Briefcase size={18} />;
    }
    return <Wrench size={18} />;
  };

  // Get role color
  const getRoleColor = (role) => {
    const roleLower = (role || "").toLowerCase();
    if (roleLower.includes("manager") || roleLower.includes("supervisor")) {
      return { bg: "rgba(239,68,68,0.15)", color: "#ef4444" };
    }
    if (roleLower.includes("senior") || roleLower.includes("lead")) {
      return { bg: "rgba(245,158,11,0.15)", color: "#f59e0b" };
    }
    return { bg: "rgba(59,130,246,0.15)", color: "#3b82f6" };
  };

  return (
    <div className="main-content">
      {/* Header */}
      <div style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        marginBottom: 24,
      }}>
        <div>
          <h1 style={{ fontSize: 28, fontWeight: 700, marginBottom: 6 }}>
            👨‍🔧 Team
          </h1>
          <p style={{ fontSize: 14, opacity: 0.7 }}>
            {filteredTeam.length} team member{filteredTeam.length !== 1 ? "s" : ""} found
          </p>
        </div>
        <Link 
          to="/add-technician" 
          className="add-button" 
          style={{ padding: "10px 18px", fontSize: 14 }}
        >
          + Add Technician
        </Link>
      </div>

      {/* Search bar */}
      <div style={{
        position: "relative",
        marginBottom: 24,
        maxWidth: 500,
      }}>
        <Search 
          size={18} 
          style={{
            position: "absolute",
            left: 14,
            top: "50%",
            transform: "translateY(-50%)",
            opacity: 0.5,
          }}
        />
        <input
          type="text"
          placeholder="Search team by name, role, or email..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{
            width: "100%",
            padding: "12px 14px 12px 44px",
            borderRadius: 12,
            border: "1px solid rgba(148,163,184,0.25)",
            background: "rgba(255,255,255,0.04)",
            fontSize: 14,
            outline: "none",
          }}
        />
      </div>

      {/* Loading state */}
      {loading ? (
        <div style={{
          padding: 60,
          textAlign: "center",
          opacity: 0.6,
        }}>
          <div style={{ fontSize: 16 }}>Loading team members...</div>
        </div>
      ) : (
        <>
          {/* Team grid */}
          <div style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(360px, 1fr))",
            gap: 16,
          }}>
            {filteredTeam.map((member) => {
              const isEditing = editingId === member.id;
              const avatarColor = getAvatarColor(member.full_name);
              const roleColors = getRoleColor(member.role);
              
              return (
                <div
                  key={member.id}
                  style={{
                    borderRadius: 16,
                    border: isEditing 
                      ? "2px solid rgba(59,130,246,0.5)" 
                      : "1px solid rgba(148,163,184,0.25)",
                    background: isEditing 
                      ? "rgba(59,130,246,0.05)" 
                      : "rgba(255,255,255,0.04)",
                    boxShadow: isEditing 
                      ? "0 8px 32px rgba(59,130,246,0.15)"
                      : "0 4px 20px rgba(0,0,0,0.08)",
                    overflow: "hidden",
                    transition: "all 0.2s ease",
                  }}
                >
                  {/* Card header with avatar */}
                  <div style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 14,
                    padding: "18px",
                    background: isEditing 
                      ? "rgba(59,130,246,0.08)" 
                      : "rgba(255,255,255,0.02)",
                    borderBottom: "1px solid rgba(148,163,184,0.15)",
                  }}>
                    {/* Avatar */}
                    <div style={{
                      width: 56,
                      height: 56,
                      borderRadius: 14,
                      background: avatarColor,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "white",
                      fontSize: 20,
                      fontWeight: 700,
                      flexShrink: 0,
                    }}>
                      {getInitials(member.full_name)}
                    </div>

                    {/* Name and ID */}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      {isEditing ? (
                        <input
                          name="full_name"
                          value={editForm.full_name}
                          onChange={handleEditChange}
                          placeholder="Full Name"
                          style={{
                            width: "100%",
                            padding: "8px 12px",
                            borderRadius: 8,
                            border: "1px solid rgba(59,130,246,0.5)",
                            background: "rgba(255,255,255,0.1)",
                            fontSize: 16,
                            fontWeight: 600,
                            outline: "none",
                          }}
                        />
                      ) : (
                        <>
                          <div style={{ 
                            fontSize: 17, 
                            fontWeight: 700, 
                            marginBottom: 4,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}>
                            {member.full_name || "No name"}
                          </div>
                          <div style={{ fontSize: 12, opacity: 0.6 }}>
                            ID: {member.id}
                          </div>
                        </>
                      )}
                    </div>

                    {/* Action buttons */}
                    <div style={{ display: "flex", gap: 6 }}>
                      {isEditing ? (
                        <>
                          <button
                            className="icon-btn"
                            onClick={() => saveEdit(member.id)}
                            title="Save"
                            type="button"
                            style={{ background: "#10b981", color: "white" }}
                          >
                            <Save size={16} />
                          </button>
                          <button
                            className="icon-btn ghost"
                            onClick={cancelEdit}
                            title="Cancel"
                            type="button"
                          >
                            <X size={16} />
                          </button>
                        </>
                      ) : (
                        <>
                          <button
                            className="icon-btn ghost"
                            onClick={() => startEdit(member)}
                            title="Edit"
                            type="button"
                          >
                            <Pencil size={16} />
                          </button>
                          <button
                            className="icon-btn danger"
                            onClick={() => deleteTech(member.id)}
                            title="Delete"
                            type="button"
                          >
                            <Trash2 size={16} />
                          </button>
                        </>
                      )}
                    </div>
                  </div>

                  {/* Card body with details */}
                  <div style={{ padding: "18px", display: "flex", flexDirection: "column", gap: 14 }}>
                    {/* Role */}
                    <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                      <div style={{
                        width: 40,
                        height: 40,
                        borderRadius: 10,
                        background: roleColors.bg,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: roleColors.color,
                        flexShrink: 0,
                      }}>
                        {getRoleIcon(member.role)}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 11, opacity: 0.6, marginBottom: 2 }}>
                          Role
                        </div>
                        {isEditing ? (
                          <input
                            name="role"
                            value={editForm.role}
                            onChange={handleEditChange}
                            placeholder="Role/Position"
                            style={{
                              width: "100%",
                              padding: "6px 10px",
                              borderRadius: 6,
                              border: "1px solid rgba(148,163,184,0.3)",
                              background: "rgba(255,255,255,0.08)",
                              fontSize: 14,
                              outline: "none",
                            }}
                          />
                        ) : (
                          <div style={{ 
                            fontSize: 14, 
                            fontWeight: 600,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}>
                            {member.role || "—"}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Email */}
                    <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                      <div style={{
                        width: 40,
                        height: 40,
                        borderRadius: 10,
                        background: "rgba(139,92,246,0.15)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: "#8b5cf6",
                        flexShrink: 0,
                      }}>
                        <Mail size={18} />
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 11, opacity: 0.6, marginBottom: 2 }}>
                          Email
                        </div>
                        {isEditing ? (
                          <input
                            name="email"
                            value={editForm.email}
                            onChange={handleEditChange}
                            placeholder="Email address"
                            type="email"
                            style={{
                              width: "100%",
                              padding: "6px 10px",
                              borderRadius: 6,
                              border: "1px solid rgba(148,163,184,0.3)",
                              background: "rgba(255,255,255,0.08)",
                              fontSize: 14,
                              outline: "none",
                            }}
                          />
                        ) : (
                          <div style={{ 
                            fontSize: 14, 
                            fontWeight: 500,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}>
                            {member.email || "—"}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Empty state */}
          {filteredTeam.length === 0 && (
            <div style={{
              padding: 60,
              textAlign: "center",
              opacity: 0.6,
            }}>
              {searchTerm ? (
                <>
                  <div style={{ fontSize: 16, marginBottom: 8 }}>
                    No team members found matching "{searchTerm}"
                  </div>
                  <div style={{ fontSize: 14 }}>
                    Try adjusting your search terms
                  </div>
                </>
              ) : (
                <>
                  <div style={{ fontSize: 16, marginBottom: 8 }}>
                    No team members yet
                  </div>
                  <div style={{ fontSize: 14 }}>
                    Click "Add Technician" to add your first team member
                  </div>
                </>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default TeamList;
